import { useAuthenticatedQuery } from '@/hooks/auth/use-auth-query-hooks';
import type { TeamMember } from '@/features/team/types/team-types';
import { TEAM_MEMBER_STATUS } from '@/features/team/types/team-enums';
import { MemberRole } from '@/services/internal-api/accounts/types/accounts-api-service-enums';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import useStore from '@/store/app/use-store';

interface TeamMemberApiUser {
  userId: string | number;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  status: string;
  role: TeamMember['role'];
}

export function useTeamMembers(
  accountId: string | undefined,
  currentUserRole: MemberRole | null,
  enabled = true
) {
  const user = useStore(s => s.user);

  const email = user?.email;

  const hasPermission = !!accountId && (currentUserRole === MemberRole.OWNER || currentUserRole === MemberRole.ADMIN)
;

  const { data, isLoading, isError, error, refetch } = useAuthenticatedQuery(
    ['teamMembers', accountId],
    async () => {
      if (!accountId) {
        return [];
      }
      const response = await AccountsApiService.getTeamMembers(accountId);
      return response.data.users.map((apiUser: TeamMemberApiUser): TeamMember => {
        const firstName = apiUser.firstName ?? '';
        const lastName = apiUser.lastName ?? '';
        const fullName = `${firstName} ${lastName}`.trim();
        return {
          id: apiUser.userId,
          name: fullName || apiUser.email,
          email: apiUser.email,
          status: apiUser.status === 'active' ? TEAM_MEMBER_STATUS.ACTIVE : TEAM_MEMBER_STATUS.PENDING,
          role: apiUser.role,
          isCurrentUser: apiUser.email === email,
        };
      });
    },
    {
      enabled: hasPermission && enabled,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    teamMembers: data ?? [],
    isLoading,
    isError,
    error,
    refetch,
  };
}
